import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    fetchClientLoyaltyCardEvents,
    fetchLoyaltyCardDetail,
    updateClientLoyaltyCard,
} from "../../../../lib/api";
import type { Client360RedCardFormValues } from "../red-card/redCardTypes";

export function useClient360Loyalty({
    profileId,
    selectedPartnerId,
    enabled = true,
}: {
    profileId: string;
    selectedPartnerId: number;
    enabled?: boolean;
}) {
    const queryClient = useQueryClient();
    const [saveError, setSaveError] = useState<string | null>(null);
    const [detailEnabled, setDetailEnabled] = useState(false);

    useEffect(() => {
        if (!enabled || selectedPartnerId <= 0) {
            setDetailEnabled(false);
            return;
        }

        if ("requestIdleCallback" in window) {
            const idleId = window.requestIdleCallback(() => setDetailEnabled(true), { timeout: 2500 });

            return () => window.cancelIdleCallback(idleId);
        }

        const timeoutId = setTimeout(() => setDetailEnabled(true), 400);

        return () => clearTimeout(timeoutId);
    }, [enabled, selectedPartnerId]);

    const eventsQuery = useQuery({
        queryKey: ["client-360-loyalty-events", profileId],
        queryFn: () => fetchClientLoyaltyCardEvents(profileId),
        enabled: enabled && profileId !== "" && detailEnabled,
        retry: 1,
        staleTime: 30_000,
    });

    const detailQuery = useQuery({
        queryKey: ["client-360-loyalty-detail", selectedPartnerId],
        queryFn: () => fetchLoyaltyCardDetail(selectedPartnerId),
        enabled: enabled && selectedPartnerId > 0 && detailEnabled,
        retry: 1,
        staleTime: 60_000,
    });

    const mutation = useMutation({
        mutationFn: (values: Client360RedCardFormValues) =>
            updateClientLoyaltyCard({
                profileId,
                cardNumber: values.cardNumber,
                phone: values.phone,
                email: values.email,
                holder: values.holder,
                district: values.district,
                discount: values.discount,
                issueCard: values.issueCard,
                gdprProcessingConsent: values.gdprProcessingConsent,
                gdprMarketingConsent: values.gdprMarketingConsent,
            }),
        onSuccess: async () => {
            setSaveError(null);
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["client-360-profile", profileId] }),
                queryClient.invalidateQueries({ queryKey: ["loyalty-dataset"] }),
                queryClient.invalidateQueries({ queryKey: ["client-360-loyalty-events", profileId] }),
                queryClient.invalidateQueries({ queryKey: ["client-360-loyalty-detail"] }),
            ]);
        },
        onError: (error: unknown) => {
            const message =
                error && typeof error === "object" && "response" in error
                    ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? "")
                    : "";

            setSaveError(message || "Неуспешен запис на картата.");
        },
    });

    return { detailQuery, eventsQuery, mutation, saveError };
}
